import {concat, differenceWith} from 'lodash';
import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {AsyncHandlerOf, Handler} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {boxedInputColorReaders} from '../../../../../libs/shared/ui/src/components/boxedInputs/boxedInputConstants';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ButtonGroupSpacer} from '../../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroupSpacer';
import {SameWidthButton} from '../../../../../libs/shared/ui/src/components/buttons/sameWidthButton';
import {Dropdown, DropdownProps} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownHeader} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownHeader';
import {fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {
  AddRecipients,
  isVisualRecipientEqualToHandle,
  RemoveRecipients,
} from '../../../../helpers/models/recipientHelpers';
import {ContactSourcesEnum} from '../../../../models/api/contactModel';
import {RecipientRolesEnum} from '../../../../models/api/recipientModel';
import {SearchTagModel} from '../../../../models/api/searchTagModel';
import {TagModel} from '../../../../models/api/tagModel';
import {VisualRecipientModel} from '../../../../models/api/visualRecipientModel';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../../../services/tracking';
import {DialogButtonGroup} from '../../dialogs/dialogButtonGroup';
import {RecipientsEditorContainer} from '../../editor/recipients/recipientsEditorContainer';
import {PickParentTagDropdownBox} from '../tags/pickParentTagDropdownBox';

/*
 * Props.
 */

interface CreateTagRuleDropdownProps extends DropdownProps {
  tag?: TagModel;
  recipients: ReadonlyArray<VisualRecipientModel>;
  userNamespace: string;
  createRule: AsyncHandlerOf<RuleTemplate>;
  onRequestClose: Handler;
}

export interface RuleTemplate {
  tag: TagModel;
  recipients: ReadonlyArray<VisualRecipientModel>;
}

/*
 * Styles.
 */

const StyledDialogContentDiv = styled.div`
  flex-grow: 1;

  display: flex;
  flex-direction: column;
`;

const StyledDialogMainContentDiv = styled.div`
  flex: 1;
  margin: 20px 20px 17px;
`;

// Fake the BoxedInput on a div
const StyledBoxDiv = styled.div`
  display: flex;

  border-radius: 4px;
  margin: 12px 0 22px;
  padding: 3px 4px;
  min-height: 32px;
  color: ${boxedInputColorReaders.text};
  background-color: ${boxedInputColorReaders.background};

  &:focus-within,
  &[focus-within] {
    background-color: ${boxedInputColorReaders.backgroundFocused};
    box-shadow: 0 0 0 1px ${(p) => p.theme.palette.blue.shade40},
      inset 0 0 0 1px ${(p) => p.theme.palette.blue.shade40};
  }
`;

const StyledTextDiv = styled.div`
  font-weight: ${fontWeights.semibold};
  font-size: 13px;
  color: ${boxedInputColorReaders.textLabel};
  grid-area: title;
  margin-bottom: -4px;
`;

/*
 * Component.
 */

export const CreateTagRuleDropdown: FC<CreateTagRuleDropdownProps> = (props) => {
  const [tag, setTag] = useState(props.tag);
  const [recipients, setRecipients] = useState(props.recipients);
  const [isSaving, setIsSaving] = useState(false);

  const onTagSelect = (newSearchTags?: SearchTagModel) => {
    setTag((newSearchTags && newSearchTags[0]) || undefined);
  };

  const onAddRecipients: AddRecipients = (newRecipients) => {
    setRecipients(concat(recipients, newRecipients));
  };

  const onRemoveRecipients: RemoveRecipients = (recipientsToRemove) => {
    const newRecipients = differenceWith(recipients, recipientsToRemove, isVisualRecipientEqualToHandle);
    setRecipients(newRecipients);
  };

  const onCreateClick = () => {
    if (!isValidState({tag, recipients, isSaving})) {
      return;
    }

    setIsSaving(true);
    const {createRule, onRequestClose} = props;

    if (tag) {
      createRule({tag, recipients});
    }

    trackInterfaceEvent(InterfaceEventsEnum.ADD_QUICK_RULE, {source: 'dropdown'});
    setIsSaving(false);
    onRequestClose();
  };

  return (
    <Dropdown {...props} interactionId="dropdown-create-rule" width={350} hasBackground>
      <DropdownHeader title={<Trans id="create-rule-title">New quick rule</Trans>} />
      <StyledDialogContentDiv>
        <StyledDialogMainContentDiv>
          <StyledTextDiv>
            <Trans id="create-rule-recipientlabel">When a new message is received from</Trans>
          </StyledTextDiv>
          <StyledBoxDiv>
            <RecipientsEditorContainer
              role={RecipientRolesEnum.FROM}
              source={ContactSourcesEnum.EMAIL}
              namespace={props.userNamespace}
              recipients={recipients}
              addRecipients={onAddRecipients}
              removeRecipients={onRemoveRecipients}
              shouldFocus={!recipients.length}
            />
          </StyledBoxDiv>
          <PickParentTagDropdownBox
            onTagSelect={onTagSelect}
            namespace={props.userNamespace}
            selectedTag={tag}
            label={<Trans id="create-rule-taglabel">Move to</Trans>}
          />
        </StyledDialogMainContentDiv>
        {renderButtons(props, onCreateClick, {tag, recipients, isSaving})}
      </StyledDialogContentDiv>
    </Dropdown>
  );
};

/*
 * Helpers.
 */

interface CreateTagRuleDropdownState {
  tag?: TagModel;
  recipients: ReadonlyArray<VisualRecipientModel>;
  isSaving?: boolean;
}

function renderButtons(
  props: CreateTagRuleDropdownProps,
  onCreateClick: Handler,
  currentState: CreateTagRuleDropdownState,
) {
  const canSave = isValidState(currentState);

  return (
    <DialogButtonGroup>
      <ButtonGroupSpacer />
      <SameWidthButton onClick={props.onRequestClose}>
        <Trans id="dropdown-create-rule-cancel">Cancel</Trans>
      </SameWidthButton>
      <SameWidthButton variant={ButtonVariantsEnum.PRIMARY} onClick={onCreateClick} isDisabled={!canSave}>
        <Trans id="dropdown-create-rule-create">Create</Trans>
      </SameWidthButton>
    </DialogButtonGroup>
  );
}

function isValidState(state: CreateTagRuleDropdownState): boolean {
  const {tag, recipients, isSaving} = state;
  return Boolean(tag && recipients.length) && !isSaving;
}
